import React, { useState, useRef, useEffect } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import "../css/ChatPage.css";
import send from "../assets/icons/send.png";
import GraphicEqIcon from "@mui/icons-material/GraphicEq";

const ImageToVideo = () => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [userImages, setUserImages] = useState({});
  const [inputImages, setInputImages] = useState([]);
  const chatEndRef = useRef(null);

  const scrollToBottom = () => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSend = () => {
    if (!input.trim() && inputImages.length === 0) return;

    const time = new Date().toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });

    const userId = Date.now();

    const userMsg = {
      id: userId,
      sender: "user",
      message: input,
      time,
    };

    setMessages((prev) => [...prev, userMsg]);

    if (inputImages.length > 0) {
      setUserImages((prev) => ({
        ...prev,
        [userId]: inputImages,
      }));
    }

    setInput("");
    setInputImages([]);

    // Bot response based on images
    const botResponse =
      inputImages.length > 0
        ? "Here’s your video download link: 🔗 https://example.com/videos/converted-video.mp4"
        : "This is a dummy bot response.";

    const botMsg = {
      id: userId + 1,
      sender: "bot",
      message: botResponse,
      time: new Date().toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      }),
    };

    setTimeout(() => {
      setMessages((prev) => [...prev, botMsg]);
    }, 500);
  };

  const handleAddImage = (msgId) => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.multiple = true;

    input.onchange = () => {
      const files = Array.from(input.files);
      const urls = files.map((file) => URL.createObjectURL(file));
      setUserImages((prev) => ({
        ...prev,
        [msgId]: [...(prev[msgId] || []), ...urls],
      }));
    };

    input.click();
  };

  const handleAttachClick = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.multiple = true;

    input.onchange = () => {
      const files = Array.from(input.files);
      const urls = files.map((file) => URL.createObjectURL(file));
      setInputImages((prev) => [...prev, ...urls]);
    };

    input.click();
  };

  const renderMessage = (msg) => {
    const isBot = msg.sender === "bot";
    const profile = isBot
      ? "https://img.freepik.com/free-vector/chatbot-chat-message-vectorart_78370-4104.jpg?semt=ais_hybrid&w=740"
      : "https://cdn-icons-png.flaticon.com/512/3135/3135715.png";
    const bubbleClass = isBot ? "bot-bubble" : "user-bubble";
    const containerAlign = isBot ? "flex-row" : "flex-row-reverse";
    const iconAlign = isBot ? "justify-content-start" : "justify-content-end";
    const iconMargin = isBot ? "ms-3" : "me-3";

    const images = userImages[msg.id] || [];

    return (
      <div
        className={`d-flex ${containerAlign} align-items-start my-3`}
        key={msg.id}
      >
        <div className="d-flex flex-column" style={{ maxWidth: "75%" }}>
          <div className={`chat-bubble ${bubbleClass}`}>
            <div className="d-flex align-items-center gap-2 mb-2">
              <img src={profile} className="chat-avatar" alt="avatar" />
              <strong>{isBot ? "Bot" : "User"}</strong>
              <small className="text-muted">{msg.time}</small>
            </div>
            <div>
              {msg.message.split(" ").map((word, idx) =>
                word.startsWith("http") ? (
                  <a
                    key={idx}
                    href={word}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: "#007bff", textDecoration: "underline" }}
                  >
                    {word}
                  </a>
                ) : (
                  <span key={idx}> {word} </span>
                )
              )}
            </div>

            {!isBot && images.length > 0 && (
              <div className="mt-3">
                <div className="main-image-container mb-2">
                  <img src={images[0]} alt="main" className="main-image" />
                </div>
                {images.length > 1 && (
                  <div className="thumbnail-container">
                    {images.slice(1).map((url, idx) => (
                      <div key={idx} className="thumbnail-wrapper">
                        <img
                          src={url}
                          alt={`thumb-${idx}`}
                          className="thumbnail-image"
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          <div
            className={`d-flex text-secondary gap-3 mt-1 ${iconAlign} ${iconMargin}`}
          >
            {isBot ? (
              <>
                <i
                  className="bi bi-volume-up"
                  role="button"
                  title="Play Sound"
                />
                <i className="bi bi-clipboard" role="button" title="Copy" />
                <i
                  className="bi bi-arrow-clockwise"
                  role="button"
                  title="Refresh"
                />
              </>
            ) : (
              <>
                <i className="bi bi-pencil" role="button" title="Edit" />
                <i className="bi bi-clipboard" role="button" title="Copy" />
                <i
                  className="bi bi-plus-square"
                  role="button"
                  title="Add Image"
                  onClick={() => handleAddImage(msg.id)}
                />
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  const handleVolumeClick = () => console.log("Volume icon clicked");
  const handleMicClick = () => console.log("Mic icon clicked");
  const handleCameraClick = () => console.log("Camera icon clicked");

  return (
    <div className="container-fluid mt-3">
      <div className="chat-box">
        {messages.map((msg) => renderMessage(msg))}
        <div ref={chatEndRef} />
      </div>

      {inputImages.length > 0 && (
        <div className="d-flex flex-wrap mb-2">
          {inputImages.map((url, idx) => (
            <div key={idx} className="image-preview-container">
              <img src={url} alt={`preview-${idx}`} className="image-preview" />
              <button
                className="image-remove-btn"
                onClick={() =>
                  setInputImages((prev) => prev.filter((_, i) => i !== idx))
                }
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="border rounded p-3 mt-2" style={{ minHeight: "120px" }}>
        <div className="mb-1">
          <input
            type="text"
            className="form-control border-0 shadow-none"
            placeholder="Welcome back to VelaKoda! How can I assist you?"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSend()}
          />
        </div>
        <hr />
        <div className="d-flex align-items-center justify-content-between p-2 rounded-3">
          <div className="d-flex gap-3">
            <button
              className="btn btn-link text-muted p-0"
              onClick={handleAttachClick}
            >
              <i className="bi bi-paperclip fs-5"></i>
            </button>
            <button
              className="btn btn-link text-muted p-0"
              onClick={handleVolumeClick}
            >
              <i className="bi bi-volume-up fs-5"></i>
            </button>
            <button
              className="btn btn-link text-muted p-0"
              onClick={handleMicClick}
            >
              <i className="bi bi-mic fs-5"></i>
            </button>
            <button
              className="btn btn-link text-muted p-0"
              onClick={handleCameraClick}
            >
              <GraphicEqIcon fontSize="medium" style={{ color: "#6c757d" }} />
            </button>
          </div>
          <button className="custom-send-btn" onClick={handleSend}>
            <img src={send} alt="send" />
          </button>
        </div>
      </div>

      <div className="text-center mt-3">
        <p>
          By Messaging, you agree to our <strong>Terms</strong> and have read
          our <strong>Privacy Policy</strong>.
        </p>
      </div>
    </div>
  );
};

export default ImageToVideo;
